import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import {
  RestaurantTag,
  RestaurantTagsResponse,
} from '@/lib/types/customer/RestaurantTags';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { ChevronLeftIcon } from '@heroicons/react/24/solid';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { STOREFRONT } from '@/lib/utils/translation';

// import defaultImage from '../../public/search/dflt_kat.png';
import clsx from 'clsx';
import { usePathname, useRouter } from 'next/navigation';
import ViewAllTagsModal from './search/ViewAllTagsModal';
import { initialTagsToShow } from '@/constant/configs';
import * as pixel from '@/lib/utils/fpixel';

type Props = {
  tagsData?: RestaurantTag[];
  flattenedTags?: RestaurantTag[];
  handleTag?: (tag: RestaurantTag, idx?: number) => void;
  isChildTagCarousel?: boolean;
  tagsResData?: RestaurantTagsResponse;
  carouselId: string;
  parentTagInSearchParams?: RestaurantTag | null;
  childTagInSearchParams?: RestaurantTag | null;
  swiperRefs?: Map<string, any>;
  setSwiperRefs?: (swiper: any, id: string) => void;
  scrollToSlideFirstTime?: boolean;
  setScrollToSlideFirstTime?: (value: boolean) => void;
};

const TagsCarousel = ({
  tagsData,
  isChildTagCarousel,
  tagsResData,
  carouselId,
  parentTagInSearchParams,
  childTagInSearchParams,
  swiperRefs = new Map<string, any>(),
  setSwiperRefs,
  scrollToSlideFirstTime = false,
  setScrollToSlideFirstTime = (value: boolean) => {
    scrollToSlideFirstTime = value;
  },
}: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const [navigationState, setNavigationState] = useState({
    isBeginning: true,
    isEnd: true,
  });
  const [showViewAllTag, setShowViewAllTag] = useState(false);
  const [swiperSlides, setSwiperSlides] = useState<RestaurantTag[]>([]);
  const [navigateToSlide, setNavigateToSlide] = useState<number | null>(null);
  const t = useTranslations();
  const swiperRefsInstance = swiperRefs.get(carouselId);

  useEffect(() => {
    if (tagsData && tagsData?.length > 0) {
      let tagList: RestaurantTag[] = [];
      let tagsNotInCarousel: RestaurantTag[] = [];
      if (tagsData && tagsData?.length > 0) {
        if (isChildTagCarousel) {
          tagList =
            tagsData.length <= initialTagsToShow
              ? tagsData
              : tagsData.slice(0, initialTagsToShow);
          const tagIDs = tagList.map((tag) => tag.id);
          tagsNotInCarousel = tagsData.filter(
            (tag) =>
              childTagInSearchParams?.id === tag.id &&
              !tagIDs.includes(childTagInSearchParams.id)
          );
        } else {
          tagList = tagsData;
        }
      }
      const updatedList = [...tagList, ...tagsNotInCarousel];
      setSwiperSlides(updatedList);
    }
  }, [tagsData]);

  const scrollToSlide = (slideIndex: number, id?: string) => {
    const swiper = swiperRefs.get(carouselId);
    if (swiper) {
      // handle slide if added from view all
      if (slideIndex >= swiper.slides.length) {
        slideIndex = swiper.slides.length; // Ensure we don't go out of bounds
      }
      swiper.slideTo(slideIndex);
    }
  };

  useEffect(() => {
    if (
      scrollToSlideFirstTime &&
      swiperRefsInstance &&
      swiperSlides?.length > 0
    ) {
      let parentTagIdx =
        tagsResData?.data?.findIndex(
          (item) => item.id === parentTagInSearchParams?.id
        ) ?? -1;

      if (parentTagIdx > -1) {
        scrollToSlide(parentTagIdx);
      }

      let childTagIdx = -1;
      if (
        swiperSlides &&
        swiperSlides.length > 0 &&
        childTagInSearchParams?.id
      ) {
        parentTagIdx = swiperSlides.findIndex(
          (item) => item.id === childTagInSearchParams?.parent_id
        );
        if (parentTagIdx > -1) {
          scrollToSlide(parentTagIdx);
        }
        childTagIdx = swiperSlides.findIndex(
          (item) => item.id === childTagInSearchParams?.id
        );
        if (childTagIdx > -1) {
          scrollToSlide(childTagIdx);
        }
      }
      setScrollToSlideFirstTime(false);
    }
  }, [
    scrollToSlideFirstTime,
    swiperRefsInstance,
    parentTagInSearchParams,
    childTagInSearchParams,
    swiperSlides,
    scrollToSlide,
    setScrollToSlideFirstTime,
    tagsResData,
  ]);

  // Handle next and previous navigation
  const handleNext = () => {
    swiperRefsInstance?.slideNext();
  };

  const handlePrev = () => {
    swiperRefsInstance?.slidePrev();
  };

  const updateSearchParams = (value?: string, idx?: number) => {
    const currentParams = new URLSearchParams(window.location.search);

    if (!value) {
      // If no value provided, remove the parameter entirely
      currentParams.delete('tag_ids');
    } else {
      const existingValue = currentParams.get('tag_ids');
      if (existingValue === value) {
        // If it's a child tag being clicked again, navigate to parent
        if (
          childTagInSearchParams &&
          value === childTagInSearchParams.id.toString() &&
          childTagInSearchParams.parent_id
        ) {
          currentParams.set(
            'tag_ids',
            childTagInSearchParams.parent_id.toString()
          );
          if (idx && idx > -1) {
            scrollToSlide(idx);
          }
        } else {
          // If it's a parent tag being clicked again, remove it
          currentParams.delete('tag_ids');
        }
      } else if (
        childTagInSearchParams &&
        childTagInSearchParams.parent_id?.toString() === value
      ) {
        currentParams.delete('tag_ids');
      } else {
        // First time selecting this tag, set it
        currentParams.set('tag_ids', value);
      }
    }

    const hrefString = `${pathname}?${currentParams.toString()}`;
    router.replace(hrefString, {
      scroll: false,
    });

    //  Option 2: Use window.history.pushState + Router Event (Not working)
    // Keep your current approach but manually trigger Next.js router awareness:
    // const url = new URL(window.location.href);
    // url.search = currentParams.toString();
    // window.history.pushState({}, '', url.toString());
    // // Trigger router to be aware of the change
    // window.dispatchEvent(new PopStateEvent('popstate'));

    // Option 4: Hybrid Approach
    // const url = new URL(window.location.href);
    // url.search = currentParams.toString();
    // window.history.pushState({}, '', url.toString());
    // setTimeout(() => {
    //   router.replace(hrefString, {
    //     scroll: false,
    //   });
    // }, 200);
  };

  const updateSlides = (tag: RestaurantTag) => {
    const currentSlides = [...swiperSlides];
    const existingIndex = currentSlides.findIndex((t) => t.id === tag.id);
    if (existingIndex > -1) {
      setNavigateToSlide(existingIndex);
    } else {
      currentSlides.push(tag);
      setSwiperSlides([...currentSlides]);
      setNavigateToSlide(currentSlides.length - 1);
    }
    setShowViewAllTag(false);
    updateSearchParams(tag.id.toString());
  };

  useEffect(() => {
    if (navigateToSlide !== null && swiperRefsInstance) {
      scrollToSlide(navigateToSlide);
      setNavigateToSlide(null);
    }
  }, [navigateToSlide, swiperRefsInstance, scrollToSlide]);

  function attachNavigationListeners(swiper: any) {
    const events = [
      'init',
      'slideChange',
      'reachBeginning',
      'reachEnd',
      'fromEdge',
      'resize',
      'update',
      'observerUpdate',
      'slidesLengthChange',
    ];

    events.forEach((event) =>
      swiper.on(event, () => {
        setNavigationState({
          isBeginning: swiper.isBeginning,
          isEnd: swiper.isEnd,
        });
      })
    );
  }

  return (
    <>
      <div className="lg:mb-15 mb-10 w-full">
        <div className="relative flex flex-col items-center justify-center lg:flex-row">
          {swiperSlides?.length > 0 && (
            <div className="w-full lg:w-[92%]">
              <Swiper
                id={carouselId}
                onSwiper={(swiper) => {
                  setSwiperRefs?.(swiper, carouselId);
                  attachNavigationListeners(swiper);
                }}
                modules={[Navigation]}
                spaceBetween={0}
                slidesPerGroup={2}
                slidesPerView={'auto'}
                className="scroll-spy-menu-slider !m-0"
              >
                {swiperSlides?.map((tag, idx) => (
                  <SwiperSlide
                    key={tag?.id}
                    className="!flex !w-auto min-w-min flex-col"
                  >
                    <div
                      className="mx-4 space-y-2 text-center"
                      role="button"
                      onClick={() => {
                        // Track parent tag clicks with Facebook Pixel
                        if (!isChildTagCarousel) {
                          pixel.customEvent('TagClick', {
                            tag_id: tag.id,
                            tag_name: tag.name,
                            url: pathname,
                          });
                        }

                        if (tag.link) {
                          window.open(tag.link, '_self');
                        } else {
                          updateSearchParams(tag.id.toString());
                        }
                      }}
                    >
                      <div
                        className={clsx(
                          isChildTagCarousel
                            ? 'h-[65px] w-[65px] md:h-[94px] md:w-[94px]'
                            : 'h-[80px] w-[80px] md:h-[114px] md:w-[114px]',
                          'relative overflow-hidden rounded-[28.7px]'
                        )}
                      >
                        {tag.media?.image?.[0]?.thumb_url ? (
                          <Image
                            src={tag.media.image[0].thumb_url}
                            alt=""
                            fill
                            className="object-cover"
                          />
                        ) : (
                          <Image
                            src={'/search/dflt_kat.png'}
                            alt="tag image"
                            fill
                            className="object-cover"
                          />
                        )}
                      </div>
                      <div
                        className={clsx(
                          isChildTagCarousel
                            ? 'text-md font-normal'
                            : 'text-lg font-bold',
                          parentTagInSearchParams?.id === tag.id ||
                            childTagInSearchParams?.id === tag.id ||
                            childTagInSearchParams?.parent_id === tag.id
                            ? 'text-primary'
                            : 'text-paragraph'
                        )}
                      >
                        {tag?.name}
                      </div>
                    </div>
                  </SwiperSlide>
                ))}

                {/* {tagsNotInCarousel?.length > 0 &&
                isChildTagCarousel &&
                tagsNotInCarousel.map((tag, idx) => (
                  <SwiperSlide
                    key={tag?.id}
                    className="!flex !w-auto min-w-min flex-col"
                  >
                    <div
                      className="mx-4 space-y-2 text-center"
                      role="button"
                      onClick={() => {
                        if (tag.link) {
                          window.open(tag.link, '_self');
                        } else {
                          updateSearchParams(tag.id.toString());
                        }
                      }}
                    >
                      <div
                        className={clsx(
                          isChildTagCarousel
                            ? 'h-[65px] w-[65px]  md:h-[94px] md:w-[94px]'
                            : 'h-[80px] w-[80px] md:h-[114px] md:w-[114px]',
                          'relative overflow-hidden rounded-[28.7px]'
                        )}
                      >
                        {tag.media?.image?.[0]?.thumb_url ? (
                          <Image
                            src={tag.media.image[0].thumb_url}
                            alt=""
                            fill
                            className="object-cover"
                          />
                        ) : (
                          <Image
                            src={defaultImage}
                            alt="tag image"
                            fill
                            className="object-cover"
                          />
                        )}
                      </div>
                      <div
                        className={clsx(
                          isChildTagCarousel
                            ? 'text-md font-normal'
                            : 'text-lg font-bold',
                          parentTagInSearchParams?.id === tag.id ||
                            childTagInSearchParams?.id === tag.id ||
                            childTagInSearchParams?.parent_id === tag.id
                            ? 'text-primary'
                            : 'text-paragraph'
                        )}
                      >
                        {tag?.name}
                      </div>
                    </div>
                  </SwiperSlide>
                ))} */}

                {isChildTagCarousel &&
                  tagsData &&
                  tagsData?.length > initialTagsToShow && (
                    <SwiperSlide className="!flex !w-auto min-w-min flex-col">
                      <div
                        className="mx-4 space-y-2 text-center"
                        role="button"
                        onClick={() => {
                          setShowViewAllTag(true);
                        }}
                      >
                        <div className="relative flex h-[115px] w-[80px] items-center justify-center overflow-hidden rounded-md md:h-[149px] md:w-[114px]">
                          <div
                            className={clsx(
                              isChildTagCarousel
                                ? 'text-md font-normal'
                                : 'text-lg font-bold',
                              'text-paragraph'
                            )}
                          >
                            {t(`${STOREFRONT.SEARCH}.view_all`)}
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  )}
              </Swiper>
            </div>
          )}

          {/* Custom Navigation Buttons */}
          <div className="custom-navigation mt-3 space-x-3 text-black lg:mt-0 lg:space-x-0">
            {!navigationState?.isBeginning && (
              <button
                onClick={() => handlePrev()}
                type="button"
                className={`custom-button-prev lg:absolute lg:left-0`}
                aria-label={t('aria.previous_tag_carousel')}
              >
                <div className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 text-center text-paragraph hover:bg-primary hover:text-white md:h-8 md:w-8">
                  <ChevronLeftIcon className="" width={16} height={16} />
                </div>
              </button>
            )}

            {!navigationState?.isEnd && (
              <button
                onClick={() => handleNext()}
                type="button"
                className={`custom-button-next lg:absolute lg:right-0`}
                aria-label={t('aria.next_tag_carousel')}
              >
                <div className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 text-center text-paragraph hover:bg-primary hover:text-white md:h-8 md:w-8">
                  <ChevronRightIcon className="" width={16} height={16} />
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
      <ViewAllTagsModal
        showViewAllTag={showViewAllTag}
        setShowViewAllTag={setShowViewAllTag}
        tagsData={tagsData}
        parentTagInSearchParams={parentTagInSearchParams}
        childTagInSearchParams={childTagInSearchParams}
        updateSearchParams={updateSearchParams}
        updateSlides={updateSlides}
      />
    </>
  );
};

export default TagsCarousel;
